import { useEffect, useState } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";

export type LinkPreview = {
  title: string | null;
  description: string | null;
  loaded: boolean;
};

// Module-level cache — survives re-renders and component remounts
const cache = new Map<string, LinkPreview>();
const inflight = new Map<string, Promise<LinkPreview>>();

const LOADING: LinkPreview = { title: null, description: null, loaded: false };

async function fetchPreview(url: string): Promise<LinkPreview> {
  try {
    const res = await apiAuthFetch(`api/link-preview?url=${encodeURIComponent(url)}`);
    if (!res.ok) return { title: null, description: null, loaded: true };
    const data = (await res.json()) as { title: string | null; description: string | null };
    return { ...data, loaded: true };
  } catch {
    return { title: null, description: null, loaded: true };
  }
}

/**
 * Non-blocking hook that fetches link metadata (title + description).
 * Returns cached data immediately if available, otherwise fetches in background.
 * `loaded` is true once the fetch completed (even if it returned nothing).
 * Only fetches for http(s) URLs; skips cite: and other protocols.
 */
export function useLinkPreview(url: string | undefined): LinkPreview {
  const [data, setData] = useState<LinkPreview>(() => {
    if (!url) return LOADING;
    return cache.get(url) ?? LOADING;
  });

  useEffect(() => {
    if (!url || (!url.startsWith("http://") && !url.startsWith("https://"))) return;
    if (cache.has(url)) {
      setData(cache.get(url)!);
      return;
    }

    let cancelled = false;

    // Deduplicate in-flight requests
    let promise = inflight.get(url);
    if (!promise) {
      promise = fetchPreview(url);
      inflight.set(url, promise);
    }

    promise.then((result) => {
      cache.set(url, result);
      inflight.delete(url);
      if (!cancelled) setData(result);
    }).catch((err) => {
      inflight.delete(url);
      ClientLogger.debug("Link preview fetch failed", {
        error: err, component: "useLinkPreview", metadata: { url },
      });
      if (!cancelled) setData({ title: null, description: null, loaded: true });
    });

    return () => { cancelled = true; };
  }, [url]);

  return data;
}
